import { ElementSearcher, ElementTracker, FindVisibleElementResult, FindElementReason } from "../element";
import { checkUrlCondition } from "@common/targeting/audience-check";
import { OnTrackCondition, OnTrackConditions } from "@common/models/v2";
import { pageInfo } from "@/stores/PageInfo";

export const defaultSearchTimeout = 1000;
export const defaultAbsentElementSearchTimeout = 2000;

export type OnTrackConditionCheckResult = {
  condition: OnTrackCondition;
  isSatisfied: boolean;
  reason?: string | null;
};

export type OnTrackConditionCheckResultWithElement = OnTrackConditionCheckResult & { element: Element | null };

export interface WatchOnTrackConditionsCallbacks {
  onResultChange?: (isOnTrack: boolean) => void;
  onConditionResultChange?: (results: OnTrackConditionCheckResult) => void;
  onTargetElementChange?: (results: OnTrackConditionCheckResultWithElement) => void;
}

// Helper function to evaluate selector conditions with fork step logic
function evaluateSelectorConditions(items: { condition: OnTrackCondition; satisfied: boolean }[]): boolean {
  const managedResults = items.filter((item) => item.condition.isManaged);
  const nonManagedResults = items.filter((item) => !item.condition.isManaged);

  // For fork steps (multiple managed selectors), use "any" logic; otherwise use "all"
  const managedSatisfied =
    managedResults.length > 1
      ? managedResults.some((item) => item.satisfied)
      : managedResults.every((item) => item.satisfied);

  const nonManagedSatisfied = nonManagedResults.every((item) => item.satisfied);

  return managedSatisfied && nonManagedSatisfied;
}

export function watchOnTrackConditions(
  onTrackConditions: OnTrackConditions,
  { onResultChange, onConditionResultChange, onTargetElementChange }: WatchOnTrackConditionsCallbacks
) {
  const elementTracker: ElementTracker = new ElementTracker();

  let stopUrlWatch: (() => void) | undefined;
  let isSelectorConditionsSatisfied = false;
  let isUrlConditionsSatisfied = false;
  let lastResult: boolean | null = null;

  const urlConditions = onTrackConditions.nonEmptyUrlConditions;
  const selectorConditions = onTrackConditions.nonEmptySelectorConditions;
  const selectors = onTrackConditions.selectors;

  if (urlConditions.length === 0) {
    isUrlConditionsSatisfied = true;
  }

  if (selectorConditions.length === 0) {
    isSelectorConditionsSatisfied = true;
  }

  const checkIsOnTrack = () => {
    const isOnTrack = isUrlConditionsSatisfied && isSelectorConditionsSatisfied;
    if (lastResult !== isOnTrack) {
      lastResult = isOnTrack;
      onResultChange?.(isOnTrack);
    }

    return isOnTrack;
  };

  const notifyConditionResults = (results: OnTrackConditionCheckResult[]) => {
    results.forEach((result) => {
      onConditionResultChange?.(result);
    });
  };

  const notifyTargetElementChange = (results: OnTrackConditionCheckResultWithElement[]) => {
    results
      .filter((result) => result.condition.isTargetSelectorCondition())
      .forEach((result) => {
        onTargetElementChange?.(result);
      });
  };

  checkIsOnTrack();

  const watchSelectorConditions = async () => {
    await elementTracker.trackElements(selectors, (data: Record<string, FindVisibleElementResult>) => {
      const elementsMap: Record<string, Element | null> = {};
      const reasonsMap: Record<string, FindElementReason> = {};

      for (const selectorKey in data) {
        if (Object.prototype.hasOwnProperty.call(data, selectorKey)) {
          elementsMap[selectorKey] = data[selectorKey].element;
          reasonsMap[selectorKey] = data[selectorKey].reason;
        }
      }

      const results = getSelectorConditionCheckResult(onTrackConditions, elementsMap, reasonsMap);
      notifyConditionResults(results);
      notifyTargetElementChange(results);

      isSelectorConditionsSatisfied = evaluateSelectorConditions(
        results.map((result) => ({ condition: result.condition, satisfied: result.isSatisfied }))
      );
      checkIsOnTrack();
    });
  };

  const watchUrlConditions = () => {
    const checkUrlConditions = () => {
      const url = pageInfo.url;
      const results = getUrlConditionCheckResult(onTrackConditions, url);
      notifyConditionResults(results);

      isUrlConditionsSatisfied = results.every((result) => result.isSatisfied);
      return checkIsOnTrack();
    };

    checkUrlConditions();

    stopUrlWatch = pageInfo.events.on("urlChanged", () => {
      checkUrlConditions();
    });
  };

  const stop = () => {
    elementTracker.stop();
    stopUrlWatch?.();
  };

  watchSelectorConditions();
  watchUrlConditions();

  return stop;
}

export async function waitForOnTrackConditions(
  onTrackConditions: OnTrackConditions,
  url: string,
  timeout?: number
): Promise<boolean> {
  const isOnTrackConditionsEmpty = onTrackConditions.isEmpty();
  const isOnTrackConditionsEnabled = onTrackConditions.isEnabled;

  if (isOnTrackConditionsEmpty || !isOnTrackConditionsEnabled) {
    return true;
  }

  const isUrlConditionsSatisfied = checkUrlConditions(onTrackConditions, url);
  if (!isUrlConditionsSatisfied) {
    return false;
  }

  return waitForSelectorCondition(onTrackConditions, timeout);
}

export type OnTrackConditionValidationResult = {
  isUrlConditionsSatisfied: boolean;
  isSelectorConditionsSatisfied: boolean;
  isEmpty: boolean;
};

function checkUrlConditions(onTrackConditions: OnTrackConditions, url: string) {
  const urlConditions = onTrackConditions.nonEmptyUrlConditions;
  const urlConditionCheckResults = urlConditions.map((urlCondition) => {
    return checkUrlCondition(urlCondition, url);
  });

  return urlConditionCheckResults.every((result) => result);
}

function getUrlConditionCheckResult(onTrackConditions: OnTrackConditions, url: string) {
  const urlConditions = onTrackConditions.nonEmptyUrlConditions;

  return urlConditions.map((urlCondition) => ({
    condition: urlCondition,
    isSatisfied: checkUrlCondition(urlCondition, url),
    reason: checkUrlCondition(urlCondition, url) ? null : "URL condition not met",
  }));
}

function getSelectorConditionCheckResult(
  onTrackConditions: OnTrackConditions,
  elements: Record<string, Element | null>,
  reasons: Record<string, FindElementReason>
) {
  const selectorConditions = onTrackConditions.nonEmptySelectorConditions;

  return selectorConditions.map((selectorCondition) => {
    const selector = selectorCondition.right_operand!;
    const elementExists = !!elements[selector];
    const operator = selectorCondition.operator || "exists";

    let isSatisfied: boolean;
    let reason: string | null = null;

    if (operator === "not_exists") {
      isSatisfied = !elementExists;
      reason = isSatisfied ? null : "Element exists but should not exist";
    } else {
      // Default to "exists" behavior
      isSatisfied = elementExists;
      reason = isSatisfied ? null : reasons[selector];
    }

    return {
      condition: selectorCondition,
      isSatisfied: isSatisfied,
      element: elements[selector],
      reason: reason,
    };
  });
}

async function waitForSelectorCondition(
  onTrackConditions: OnTrackConditions,
  searchTimeout?: number,
  absentSearchTimeout?: number
): Promise<boolean> {
  const elementSearcher = new ElementSearcher();
  const _timeout = searchTimeout ?? defaultSearchTimeout;
  const _absentTimeout = absentSearchTimeout ?? defaultAbsentElementSearchTimeout;

  const selectorConditions = onTrackConditions.nonEmptySelectorConditions;
  const selectorSearchPromises = selectorConditions.map((selectorCondition) => {
    const selector = selectorCondition.right_operand!;
    const operator = selectorCondition.operator || "exists";

    if (operator === "not_exists") {
      return elementSearcher.waitForElementNotPresent(selector, _absentTimeout);
    } else {
      return elementSearcher.waitForElement(selector, _timeout).then((result) => !!result.element);
    }
  });
  const selectorSearchResults = await Promise.all(selectorSearchPromises);

  // Use the same helper function as watchOnTrackConditions
  const items = selectorConditions.map((condition, index) => ({
    condition,
    satisfied: !!selectorSearchResults[index],
  }));

  return evaluateSelectorConditions(items);
}
